import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  DeleteOutline,
  EditOutlined,
  InfoOutlined,
  MoreVert,
  PeopleAltOutlined,
  SearchRounded,
} from '@mui/icons-material';
import { getUsers, deleteUser, getRestaurantes } from '../services/api';

const PRODUCT_LABELS = [
  { key: 'gap',           label: 'GAP'              },
  { key: 'atendabot',     label: 'Atendabot'        },
  { key: 'clube_mesa',    label: 'Clube de Mesa'    },
  { key: 'reservo',       label: 'Reservo'          },
  { key: 'sales_report',  label: 'DataChef'         },
  { key: 'feedback_hero', label: 'Feedback Hero'    },
  { key: 'smart',         label: 'Smart Campaigns'  },
  { key: 'checklist',     label: 'cheflist'         },
  { key: 'cv',            label: 'CurrículoCerto'   },
  { key: 'cardapio',      label: 'One Menu'         },
  { key: 'confereai',     label: 'ConfereAI'        },
  { key: 'crm',            label: 'ClientCenter CRM' },
  { key: 'linkpage',      label: 'LinkPage'         },
];

const COLUMN_HELPERS = {
  usuario:    'Login utilizado para acessar o sistema.',
  restaurante:'Empresa ou unidade vinculada ao usuário.',
  permissao:  'Define o nível de acesso do usuário no sistema.',
  plano:      'Ativo valida cobrança normalmente. Isento ignora a validação de pagamento.',
  produtos:   'Produtos e módulos liberados para este usuário.',
  acoes:      'Opções disponíveis para gerenciar este usuário.',
};

const HEADER_CELLS = [
  { id: 'usuario',    label: 'Usuário',         align: 'left',  width: '20%' },
  { id: 'restaurante',label: 'Estabelecimento', align: 'left',  width: '22%' },
  { id: 'permissao',  label: 'Permissão',        align: 'left',  width: '14%' },
  { id: 'plano',      label: 'Plano',            align: 'left',  width: '12%' },
  { id: 'produtos',   label: 'Produtos',         align: 'left',  width: '22%' },
  { id: 'acoes',      label: 'Ações',            align: 'right', width: '10%' },
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

const SECTION_ICON_BOX_SX = {
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)',
  color: '#6366f1',
  flexShrink: 0,
  border: '1px solid rgba(99,102,241,0.18)',
};

const AVATAR_PALETTE = ['#6366f1', '#0891b2', '#059669', '#d97706', '#7c3aed', '#db2777', '#0f766e'];
const getAvatarColor = (name = '') =>
  AVATAR_PALETTE[(name.charCodeAt(0) || 0) % AVATAR_PALETTE.length];

const badgeChipSx = {
  height: 26,
  fontWeight: 700,
  fontSize: '0.72rem',
  borderRadius: '999px',
  border: '1px solid transparent',
  '& .MuiChip-label': { px: 1.1 },
};

const productChipSx = {
  height: 26,
  fontWeight: 600,
  fontSize: '0.74rem',
  borderRadius: '999px',
  '& .MuiChip-label': { px: 1.2 },
};

const tooltipProps = {
  tooltip: {
    sx: {
      bgcolor: '#101828',
      color: '#f8fafc',
      fontSize: '0.75rem',
      fontWeight: 500,
      px: 1.2,
      py: 0.9,
      borderRadius: 2,
      boxShadow: '0 12px 28px rgba(15,23,42,0.18)',
      maxWidth: 220,
    },
  },
  arrow: { sx: { color: '#101828' } },
};

const HeaderLabel = ({ id, label, align = 'left' }) => (
  <Stack
    direction="row"
    spacing={0.4}
    alignItems="center"
    justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
    sx={{ width: '100%' }}
  >
    <Typography component="span" sx={{ fontSize: '0.775rem', fontWeight: 700, letterSpacing: '0.01em', color: '#475467' }}>
      {label}
    </Typography>
    <Tooltip title={COLUMN_HELPERS[id]} arrow enterTouchDelay={0} componentsProps={tooltipProps}>
      <IconButton
        size="small"
        aria-label={`Informações sobre ${label}`}
        sx={{
          p: 0, ml: 0.5, width: 18, height: 18,
          color: '#98a2b3',
          border: '1px solid rgba(152,162,179,0.35)',
          bgcolor: 'rgba(255,255,255,0.92)',
          '&:hover': { color: '#667085', bgcolor: '#fff' },
        }}
      >
        <InfoOutlined sx={{ fontSize: 11 }} />
      </IconButton>
    </Tooltip>
  </Stack>
);

const RowActions = ({ user, onEdit, onDelete, active, onMenuOpen, onMenuClose }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (e) => { setAnchorEl(e.currentTarget); onMenuOpen(user.id); };
  const handleClose = () => { setAnchorEl(null); onMenuClose(user.id); };

  return (
    <>
      <Tooltip title="Abrir ações">
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={`Ações do usuário ${user.username}`}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
          sx={{
            border: '1px solid',
            borderColor: active ? '#c7d2fe' : '#e2e8f0',
            borderRadius: '10px',
            width: 36, height: 36,
            bgcolor: active ? '#eef2ff' : '#fff',
            color: active ? '#4f46e5' : '#64748b',
            boxShadow: active ? '0 4px 12px rgba(99,102,241,0.18)' : '0 1px 2px rgba(16,24,40,0.04)',
            transition: 'all 150ms ease',
            '&:hover': { bgcolor: active ? '#eef2ff' : '#f8fafc', borderColor: active ? '#a5b4fc' : '#94a3b8', color: active ? '#4f46e5' : '#344054' },
          }}
        >
          <MoreVert sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '14px',
            border: '1px solid rgba(15,23,42,0.06)',
            boxShadow: '0 16px 40px rgba(15,23,42,0.12)',
            minWidth: 165,
            mt: 0.5,
          },
        }}
      >
        <MenuItem onClick={() => { handleClose(); onEdit(user); }} sx={{ gap: 1.25, py: 1.1, fontSize: '0.875rem', fontWeight: 500 }}>
          <EditOutlined sx={{ fontSize: 17 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); onDelete(user.id); }} sx={{ gap: 1.25, py: 1.1, color: 'error.main', fontSize: '0.875rem', fontWeight: 500 }}>
          <DeleteOutline sx={{ fontSize: 17 }} />
          Excluir
        </MenuItem>
      </Menu>
    </>
  );
};

const UsersTable = ({
  items, onEdit, onDelete, getRestauranteNome,
  currentPage, rowsPerPage, totalFilteredUsers, totalPages,
  pageStartIndex, pageEndIndex, visiblePageNumbers,
  onPageChange, onRowsPerPageChange,
}) => {
  const [activeMenuUserId, setActiveMenuUserId] = useState(null);

  return (
    <Box sx={{ ...CARD_SX }}>
      <TableContainer sx={{ px: { xs: 1, md: 1.5 }, pt: { xs: 1.25, md: 1.5 }, pb: 0 }}>
        <Table sx={{ minWidth: 980, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              {HEADER_CELLS.map((hc) => (
                <TableCell key={hc.id} align={hc.align} sx={{ width: hc.width, borderBottom: '1px solid #eaecf0', py: 1.9, px: 2.5 }}>
                  <HeaderLabel id={hc.id} label={hc.label} align={hc.align} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={HEADER_CELLS.length} align="center" sx={{ py: 10, border: 0 }}>
                  <Typography sx={{ color: '#94a3b8', fontWeight: 500, fontSize: '0.9rem' }}>
                    Nenhum usuário encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {items.map((user) => {
              const userProducts = PRODUCT_LABELS.filter((p) => user[p.key]).map((p) => p.label);
              const visibleProducts = userProducts.slice(0, 2);
              const extraProducts = userProducts.length - visibleProducts.length;
              const isActive = activeMenuUserId === user.id;

              return (
                <TableRow
                  key={user.id}
                  sx={{
                    '& td': {
                      py: 1.85, px: 2.5,
                      borderBottom: '1px solid',
                      borderColor: isActive ? '#e0e7ff' : '#f1f5f9',
                      verticalAlign: 'middle',
                      bgcolor: isActive ? '#f5f7ff' : '#fff',
                      transition: 'background-color 140ms ease, border-color 140ms ease',
                    },
                    '& td:first-of-type': {
                      borderLeft: '3px solid',
                      borderLeftColor: isActive ? '#6366f1' : 'transparent',
                      transition: 'border-left-color 140ms ease',
                    },
                    '&:hover td': { bgcolor: isActive ? '#f5f7ff' : '#fafbff', borderColor: isActive ? '#e0e7ff' : '#e8eef5' },
                    '&:hover td:first-of-type': { borderLeftColor: isActive ? '#6366f1' : '#c7d2fe' },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '50%',
                        bgcolor: getAvatarColor(user.username),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '0.67rem', fontWeight: 800, flexShrink: 0, letterSpacing: '0.03em',
                      }}>
                        {(user.username || '?').slice(0, 2).toUpperCase()}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#101828', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {user.username}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#475467', fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {getRestauranteNome(user.restaurante_id)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={user.is_admin ? 'Administrador' : user.is_owner ? 'Owner' : 'Usuário'}
                      size="small"
                      sx={{
                        ...badgeChipSx,
                        bgcolor: user.is_admin ? '#eef2ff' : user.is_owner ? '#ecfdf3' : '#f8fafc',
                        color: user.is_admin ? '#4338ca' : user.is_owner ? '#027a48' : '#475467',
                        borderColor: user.is_admin ? '#c7d2fe' : user.is_owner ? '#abefc6' : '#eaecf0',
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={user.billing_exempt ? 'Isento' : 'Ativo'}
                      size="small"
                      sx={{ ...badgeChipSx, bgcolor: user.billing_exempt ? '#fffbeb' : '#f0fdf4', color: user.billing_exempt ? '#b54708' : '#166534', borderColor: user.billing_exempt ? '#fde68a' : '#bbf7d0' }}
                    />
                  </TableCell>

                  <TableCell>
                    {userProducts.length > 0 ? (
                      <Tooltip title={userProducts.join(', ')} arrow enterTouchDelay={0}>
                        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ minWidth: 0 }}>
                          {visibleProducts.map((product) => (
                            <Chip
                              key={`${user.id}-${product}`}
                              label={product}
                              size="small"
                              sx={{ ...productChipSx, maxWidth: 108, bgcolor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', '& .MuiChip-label': { px: 1.2, overflow: 'hidden', textOverflow: 'ellipsis' } }}
                            />
                          ))}
                          {extraProducts > 0 && (
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.8rem' }}>
                              +{extraProducts}
                            </Typography>
                          )}
                        </Stack>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#cbd5e1', fontStyle: 'italic', fontSize: '0.82rem' }}>
                        Nenhum
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    <RowActions
                      user={user}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      active={isActive}
                      onMenuOpen={setActiveMenuUserId}
                      onMenuClose={(id) => { if (activeMenuUserId === id) setActiveMenuUserId(null); }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, borderTop: '1px solid rgba(15,23,42,0.06)', bgcolor: '#fcfdff', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', lg: 'center' }, gap: 1.5 }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#64748b' }}>
          {totalFilteredUsers === 0
            ? 'Mostrando 0 de 0 usuários'
            : `Mostrando ${pageStartIndex}–${pageEndIndex} de ${totalFilteredUsers} usuários`}
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ width: { xs: '100%', lg: 'auto' } }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#64748b', whiteSpace: 'nowrap' }}>Por página</Typography>
            <TextField select size="small" value={rowsPerPage} onChange={(e) => onRowsPerPageChange(Number(e.target.value))} sx={{ width: 88, '& .MuiOutlinedInput-root': { height: 34, bgcolor: '#fff', borderRadius: '10px' } }}>
              {ROWS_PER_PAGE_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || totalFilteredUsers === 0} sx={{ minWidth: 82, height: 34, borderRadius: '10px', fontWeight: 600, fontSize: '0.82rem', borderColor: '#e2e8f0', color: '#344054', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}>
              Anterior
            </Button>
            {visiblePageNumbers.map((n) => (
              <Button key={n} size="small" variant="outlined" onClick={() => onPageChange(n)} sx={{ minWidth: 34, width: 34, height: 34, borderRadius: '10px', fontWeight: 700, boxShadow: 'none', fontSize: '0.82rem', ...(n === currentPage ? { bgcolor: '#6366f1', borderColor: '#6366f1', color: '#fff', '&:hover': { bgcolor: '#4f46e5', borderColor: '#4f46e5' } } : { borderColor: '#e2e8f0', color: '#344054', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }) }}>
                {n}
              </Button>
            ))}
            <Button size="small" variant="outlined" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || totalFilteredUsers === 0} sx={{ minWidth: 78, height: 34, borderRadius: '10px', fontWeight: 600, fontSize: '0.82rem', borderColor: '#e2e8f0', color: '#344054', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}>
              Próximo
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [restaurantes, setRestaurantes] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedRestaurante, setSelectedRestaurante] = useState('');
  const [selectedPermission, setSelectedPermission] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setUsers([]);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const fetchRestaurantes = async () => {
      try {
        const data = await getRestaurantes();
        setRestaurantes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setRestaurantes([]);
      }
    };
    fetchRestaurantes();
  }, []);

  const restaurantesMap = useMemo(
    () => Object.fromEntries(restaurantes.map((r) => [String(r.id), r.nome])),
    [restaurantes],
  );
  const getRestauranteNome = useCallback((id) => restaurantesMap[String(id)] || '-', [restaurantesMap]);
  const restauranteOptions = useMemo(
    () => restaurantes.map((r) => ({ value: String(r.id), label: r.nome })).sort((a, b) => a.label.localeCompare(b.label)),
    [restaurantes],
  );

  const filteredUsers = useMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    return users.filter((user) => {
      const restauranteNome = getRestauranteNome(user.restaurante_id).toLowerCase();
      const restauranteId = String(user.restaurante_id || '');
      const permission = user.is_admin ? 'administrador' : user.is_owner ? 'owner' : 'usuário';
      const plan = user.billing_exempt ? 'isento' : 'ativo';
      const products = PRODUCT_LABELS.filter((p) => user[p.key]).map((p) => p.label.toLowerCase()).join(' ');
      const matchesSearch = !query || String(user.username || '').toLowerCase().includes(query) || restauranteNome.includes(query) || permission.includes(query) || plan.includes(query) || products.includes(query);
      const matchesRestaurante = !selectedRestaurante || restauranteId === selectedRestaurante;
      const matchesPermission = !selectedPermission
        || (selectedPermission === 'admin' && user.is_admin)
        || (selectedPermission === 'owner' && user.is_owner)
        || (selectedPermission === 'user' && !user.is_admin && !user.is_owner);
      const matchesPlan = !selectedPlan || (selectedPlan === 'isento' && user.billing_exempt) || (selectedPlan === 'ativo' && !user.billing_exempt);
      return matchesSearch && matchesRestaurante && matchesPermission && matchesPlan;
    });
  }, [getRestauranteNome, search, selectedPlan, selectedPermission, selectedRestaurante, users]);

  const hasActiveFilters = Boolean(String(search || '').trim() || selectedRestaurante || selectedPermission || selectedPlan);

  const activeFilterChips = [
    String(search || '').trim() ? { key: 'search', label: `Busca: ${String(search).trim()}`, onDelete: () => setSearch('') } : null,
    selectedRestaurante ? { key: 'restaurante', label: `Estabelecimento: ${restauranteOptions.find((o) => o.value === selectedRestaurante)?.label || '-'}`, onDelete: () => setSelectedRestaurante('') } : null,
    selectedPermission ? {
      key: 'permission',
      label: `Permissão: ${selectedPermission === 'admin' ? 'Administrador' : selectedPermission === 'owner' ? 'Owner' : 'Usuário'}`,
      onDelete: () => setSelectedPermission(''),
    } : null,
    selectedPlan ? { key: 'plan', label: `Plano: ${selectedPlan === 'isento' ? 'Isento' : 'Ativo'}`, onDelete: () => setSelectedPlan('') } : null,
  ].filter(Boolean);

  const clearAllFilters = () => { setSearch(''); setSelectedRestaurante(''); setSelectedPermission(''); setSelectedPlan(''); };

  useEffect(() => { setCurrentPage(1); }, [search, selectedPlan, selectedPermission, selectedRestaurante]);

  const summaryStats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => !u.billing_exempt).length,
    exempt: users.filter((u) => u.billing_exempt).length,
  }), [users]);

  const totalFilteredUsers = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredUsers / rowsPerPage));

  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [currentPage, filteredUsers, rowsPerPage]);

  const pageStartIndex = totalFilteredUsers === 0 ? 0 : ((currentPage - 1) * rowsPerPage) + 1;
  const pageEndIndex = totalFilteredUsers === 0 ? 0 : Math.min(currentPage * rowsPerPage, totalFilteredUsers);

  const visiblePageNumbers = useMemo(() => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + maxVisible - 1;
    if (end > totalPages) { end = totalPages; start = end - maxVisible + 1; }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  const handlePageChange = (n) => { if (n < 1 || n > totalPages) return; setCurrentPage(n); };
  const handleRowsPerPageChange = (value) => { setRowsPerPage(value); setCurrentPage(1); };
  const handleEdit = (user) => navigate(`/manager/users/${user.id}`, { state: user });
  const handleDelete = async (id) => {
    if (!window.confirm('Excluir registro?')) return;
    await deleteUser(id);
    load();
  };

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 3, md: 4 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>

          {/* ── Header card ── */}
          <Box sx={CARD_SX}>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" gap={2} mb={2.5}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Box sx={SECTION_ICON_BOX_SX}>
                    <PeopleAltOutlined sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: { xs: '1.25rem', md: '1.4rem' }, letterSpacing: '-0.02em', lineHeight: 1.2, mb: 0.25 }}>
                      Usuários
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Gerencie acessos, permissões e produtos liberados para cada usuário.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/manager/users/new')}
                  sx={{
                    borderRadius: '12px', px: 2.5, py: 1, fontWeight: 700, boxShadow: 'none', letterSpacing: '0.01em', flexShrink: 0,
                    bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' },
                    transition: 'all 150ms ease',
                  }}
                >
                  Novo Usuário
                </Button>
              </Stack>

              <Divider sx={{ mb: 2.5, borderColor: 'rgba(15,23,42,0.06)' }} />

              {/* Stats */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems="center"
                justifyContent="center"
                divider={<Box sx={{ width: { xs: '60px', md: 1 }, height: { xs: 1, md: 40 }, bgcolor: 'rgba(15,23,42,0.06)' }} />}
                spacing={{ xs: 2, md: 0 }}
              >
                {[
                  { value: summaryStats.total,  label: 'Usuários cadastrados', color: '#0f172a'  },
                  { value: summaryStats.active, label: 'Ativos',               color: '#166534', px: { md: 5 } },
                  { value: summaryStats.exempt, label: 'Isentos',              color: '#b54708', px: { md: 5 } },
                ].map(({ value, label, color, px }) => (
                  <Box key={label} sx={{ flex: 1, textAlign: 'center', px: px || { md: 3 } }}>
                    <Typography sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 800, color, lineHeight: 1.15, mb: 0.25 }}>
                      {value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8' }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* ── Filters card ── */}
          <Box sx={{ ...CARD_SX, overflow: 'visible' }}>
            <Box sx={{ p: { xs: 1.75, md: 2.25 } }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 1.6fr) repeat(3, minmax(150px, 0.75fr))' }, gap: 1.25, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por login, estabelecimento, permissão, plano ou produto"
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" color="action" /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { height: 42, bgcolor: '#fff', borderRadius: '12px' } }}
                />
                <TextField select label="Estabelecimento" size="small" value={selectedRestaurante} onChange={(e) => setSelectedRestaurante(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { height: 42, bgcolor: '#fff', borderRadius: '12px' } }}>
                  <MenuItem value="">Todos</MenuItem>
                  {restauranteOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select label="Permissão" size="small" value={selectedPermission} onChange={(e) => setSelectedPermission(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { height: 42, bgcolor: '#fff', borderRadius: '12px' } }}>
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                  <MenuItem value="owner">Owner</MenuItem>
                  <MenuItem value="user">Usuário</MenuItem>
                </TextField>
                <TextField select label="Plano" size="small" value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { height: 42, bgcolor: '#fff', borderRadius: '12px' } }}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="ativo">Ativo</MenuItem>
                  <MenuItem value="isento">Isento</MenuItem>
                </TextField>
              </Box>

              {hasActiveFilters && (
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap mt={1.5}>
                  {activeFilterChips.map((fc) => (
                    <Chip key={fc.key} label={fc.label} onDelete={fc.onDelete} size="small" sx={{ ...badgeChipSx, bgcolor: '#f1f5f9', color: '#344054', borderColor: '#e2e8f0' }} />
                  ))}
                  <Button size="small" onClick={clearAllFilters} sx={{ minWidth: 'auto', px: 1, fontWeight: 700, fontSize: '0.8rem', color: '#6366f1', '&:hover': { bgcolor: '#eef2ff' } }}>
                    Limpar tudo
                  </Button>
                </Stack>
              )}
            </Box>
          </Box>

          {/* ── Table ── */}
          <UsersTable
            items={paginatedUsers}
            onEdit={handleEdit}
            onDelete={handleDelete}
            getRestauranteNome={getRestauranteNome}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            totalFilteredUsers={totalFilteredUsers}
            totalPages={totalPages}
            pageStartIndex={pageStartIndex}
            pageEndIndex={pageEndIndex}
            visiblePageNumbers={visiblePageNumbers}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default UsersPage;
